import React, { useEffect, useState } from 'react'
import API from '../../services/API'
import DemoWelcomeModal from './DemoWelcomeModal'
import DemoPersonaPicker from './DemoPersonaPicker'

// Mounted from App.js (next to LegalAgreementModal) only when
// tenantInfo?.isDemo and the user is logged in. Fetches the demo session
// state once, shows the blocking welcome modal on a visitor's first login,
// and hosts the persona picker that DemoSwitchButton (rendered in the
// MainFrame header and every portal layout) opens via openDemoPersonaPicker().

const OPEN_PICKER_EVENT = 'ambunet:open-demo-persona-picker'

// Called from anywhere (e.g. DemoSwitchButton) to open the persona picker
// without needing a shared context provider.
export const openDemoPersonaPicker = () =>
  window.dispatchEvent(new Event(OPEN_PICKER_EVENT))

// The login response already includes the demo session info — Login.jsx can
// prime it here so the disclaimer shows immediately instead of after the
// /demo/session-info round trip.
let primedDemoInfo = null
export const primeDemoSessionInfo = (demo) => {
  primedDemoInfo = demo
}

// Anonymous (no-signup) sessions have needsDisclaimer: null — the server
// can't track acknowledgement per visitor, so the client remembers it in
// localStorage instead. Credentialed demo sessions stay server-driven.
const DISCLAIMER_ACCEPTED_KEY = 'ambunet.demo.disclaimerAccepted'

const hasAcceptedAnonymousDisclaimer = () => {
  try {
    return !!window.localStorage.getItem(DISCLAIMER_ACCEPTED_KEY)
  } catch (e) {
    // localStorage unavailable — showing the disclaimer every visit is the
    // safe fallback.
    return false
  }
}

const markAnonymousDisclaimerAccepted = () => {
  try {
    window.localStorage.setItem(DISCLAIMER_ACCEPTED_KEY, '1')
  } catch (e) {
    // Non-fatal — worst case the disclaimer shows again next visit.
  }
}

const DemoExperience = () => {
  const [demoInfo, setDemoInfo] = useState(null)
  const [welcomeOpen, setWelcomeOpen] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)

  useEffect(() => {
    let cancelled = false

    const apply = (info) => {
      if (cancelled || !info?.isDemoSession) return
      setDemoInfo(info)
      if (info.anonymous) {
        if (!hasAcceptedAnonymousDisclaimer()) setWelcomeOpen(true)
      } else if (info.needsDisclaimer) {
        setWelcomeOpen(true)
      }
    }

    if (primedDemoInfo) {
      apply(primedDemoInfo)
      primedDemoInfo = null
      return () => {
        cancelled = true
      }
    }

    API.post('/demo/session-info', {}, { skipGlobalErrorHandler: true })
      .then((res) => {
        // 401/404 mean "not a demo session" — API never rejects on those.
        if (res.status === 200) apply(res.data)
      })
      .catch((e) => console.log(e))

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const handleOpen = () => setPickerOpen(true)
    window.addEventListener(OPEN_PICKER_EVENT, handleOpen)
    return () => window.removeEventListener(OPEN_PICKER_EVENT, handleOpen)
  }, [])

  if (!demoInfo) return null

  return (
    <>
      <DemoWelcomeModal
        open={welcomeOpen}
        onClose={() => setWelcomeOpen(false)}
        personas={demoInfo.personas}
        currentPersonaKey={demoInfo.personaKey}
        anonymous={!!demoInfo.anonymous}
        onAcknowledged={() => {
          if (demoInfo.anonymous) markAnonymousDisclaimerAccepted()
        }}
      />
      <DemoPersonaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        personas={demoInfo.personas}
        currentPersonaKey={demoInfo.personaKey}
      />
    </>
  )
}

export default DemoExperience
